import {
  AggregateUsageEvent,
  IUsageAggregateEventListRequestSchema,
  IUsageEvent,
  IUsageEventListRequestSchema,
} from '@monetize/types/app';
import {
  ApiListInfiniteScrollResponse,
  GetListApiConfig,
} from '@monetize/types/common';
import {
  UndefinedInitialDataInfiniteOptions,
  useInfiniteQuery,
  useMutation,
  UseMutationOptions,
} from '@tanstack/react-query';
import { apiGet, apiPut } from './axios';
import { accountServiceQueryKeys } from './queryKeysService';
import { composeGetQuery } from './utils';

export function useGetUsageRecordsInfiniteLoad(
  query: IUsageEventListRequestSchema,
  config: GetListApiConfig,
  options: Partial<
    UndefinedInitialDataInfiniteOptions<
      ApiListInfiniteScrollResponse<IUsageEvent>
    >
  > = {},
) {
  const params = composeGetQuery(config, {
    ...query,
    usageTypeIds: query.usageTypeIds ? query.usageTypeIds.join(',') : '',
  });

  return useInfiniteQuery<ApiListInfiniteScrollResponse<IUsageEvent>>({
    queryKey: accountServiceQueryKeys.usage.usageList(query),
    queryFn: ({ pageParam = undefined }) =>
      apiGet<ApiListInfiniteScrollResponse<IUsageEvent>>(`/usage/events`, {
        params: {
          ...params,
          nextPageToken: pageParam,
        },
      }).then((res) => res.data),
    initialPageParam: null,
    getNextPageParam: (lastPage) => lastPage.pageable?.nextPageToken,
    retry: 0,
    ...options,
  });
}

export function useVoidUsageEvent(
  options: Partial<UseMutationOptions<void, unknown, { id: string }>> = {},
) {
  return useMutation<void, unknown, { id: string }>({
    mutationFn: ({ id }) =>
      apiPut<void>(`/usage/events/${id}/void`).then((res) => res.data),
    ...options,
  });
}

export function useGetUsageAggregateRecordsInfiniteLoad(
  query: IUsageAggregateEventListRequestSchema,
  config: GetListApiConfig,
  options: Partial<
    UndefinedInitialDataInfiniteOptions<
      ApiListInfiniteScrollResponse<AggregateUsageEvent>
    >
  > = {},
) {
  const params = composeGetQuery(config, {
    ...query,
    accountIds: query.accountIds ? query.accountIds.join(',') : '',
    subscriptionIds: query.subscriptionIds
      ? query.subscriptionIds.join(',')
      : '',
    usageTypeIds: query.usageTypeIds ? query.usageTypeIds.join(',') : '',
  });

  return useInfiniteQuery<ApiListInfiniteScrollResponse<AggregateUsageEvent>>({
    queryKey: accountServiceQueryKeys.usage.usageAggregateList(query),
    queryFn: ({ pageParam = undefined }) =>
      apiGet<ApiListInfiniteScrollResponse<AggregateUsageEvent>>(
        `/usage/consumption/aggregate`,
        {
          params: {
            ...params,
            nextPageToken: pageParam,
          },
        },
      ).then((res) => res.data),
    initialPageParam: null,
    getNextPageParam: (lastPage) => lastPage.pageable?.nextPageToken,
    retry: 0,
    ...options,
  });
}
